/** 秒杀商品状态 */
export enum ActivityStatus {
  NOSTART = -1, // 未开始
  ENDED = 0, // 已结束
  UPCOMING = 1, // 预热中
  REVEALING = 2, // 揭晓
  ACTIVE = 3, // 抢购中
}

/** 活动状态：1=预热，2=揭晓，3=进行中 */
export enum ProductStatus {
  DISABLED = 'DISABLED', // 未开始（活动未开始或商品未开放秒杀）
  ENABLED = 'ENABLED', //可以抢（可参与秒杀）
  NOSTOCK = 'NOSTOCK', //已售罄（库存已空）
  QUEUEING = 'QUEUEING', // 排队中（用户正在排队等待）
  QUEUED = 'QUEUED', // 已排队（用户已完成排队，等待处理结果）
  NEEDPAY = 'NEEDPAY', // 待支付（抢购成功，待支付）
  SECKILLED = 'SECKILLED', // 已抢到（已完成支付）
}

//用户支付状态
export enum UserPayStatus {
  UNPAID = 0, // 未支付（订单已创建但未支付）
  PAID = 1, // 已支付（支付成功）
  CANCELLED = 2, // 已取消
}

// ========== 5.2 公共请求头（调用方需保证 Authorization，本 service 可追加 X-Request-ID 等） ==========

// ========== 5.3 查询秒杀商品列表 ==========

export interface SecKillingConfigProduct {
  preShowUrl: string;
  preShowMinutes?: number;
  preStartMinutes: number;
  preStartUrl: string;
  timePeriod: string;
  nowTime: number;
  secKillingStatus: 1 | 2 | 3; // 1=可以抢 2=排队中 3=已抢到
}

export interface CouponProduct {
  couponId: number;
  name: string;
  amount?: number;
  description?: string;
  imageUrl: string;
  couponTplId?: string;
}

export interface SeckillInstructionSection {
  title: string;
  content: string;
}

export interface SeckillInstruction {
  title: string;
  sections: SeckillInstructionSection[];
}

export interface ProductListItem {
  /** 优惠券信息 */
  coupon: CouponProduct;
  /** 商品ID，下单时使用 */
  productId: string;
  /** 商品名称 */
  productName: string;
  /** 商品描述 */
  description: string;
  /** 原价（分） */
  originalPrice: number;
  /** 秒杀价（分） */
  seckillPrice: number;
  /** 商品图片URL */
  imageUrl: string;
  /** HEX虚拟商品ID */
  hexProductCode: string;
  /** 用户秒杀状态 */
  secKillingStatus: ProductStatus;
  /** 限制兑换数量 */
  exchangeLimit: number;
  /** 支付积分 */
  points: number;
  /** 支付金额（分） */
  payPrice: number;
  /** 原价（分） */
  price: number;
  /** 总库存 */
  totalStock: number;
  /** 当前剩余库存 */
  stock: number;
  /** 用户是否已参与该商品秒杀 */
  userParticipated: boolean;
  /** 用户支付状态 */
  userPayStatus: UserPayStatus;
  /** 用户CRM订单ID */
  userCrmOrderId: number;
  /** 用户hex订单ID */
  userHexOrderId: string;
  /** 待支付剩余秒数 */
  payDeadLine: number | null;
  /** 秒杀活动说明 */
  secKillDesc?: string;
}

export interface ProductListPayload {
  /** 活动ID，下单时使用，为空时当前没有要展示的活动 */
  actId?: string | null;
  /** 租户ID */
  tenantId: number;
  /** 活动名称（简体） */
  name: string;
  /** 活动名称（繁体） */
  nameTc: string;
  /** 活动名称（英文） */
  nameEn: string;
  /** 活动状态：1=预热，2=揭晓，3=进行中 */
  status: ActivityStatus;
  /** 开始时间戳（秒） */
  startTime: number;
  /** 结束时间戳（秒） */
  endTime: number;
  /** 预热提前时间（分钟） */
  preShowMinutes: number;
  /** 揭晓提前时间（分钟） */
  preStartMinutes: number;
  /** Banner 图片（简体） */
  imageUrlsSc: string[];
  /** Banner 图片（繁体） */
  imageUrlsTc: string[];
  /** Banner 图片（英文） */
  imageUrlsEn: string[];
  /** Banner 图片 */
  imageUrls: string[];
  /** 秒杀时间段 */
  timePeriod: string;
  /** 下次秒杀时间，为空没有下次秒杀 */
  nextTime?: number;
  /** 服务器当前时间戳（秒） */
  nowTime: number;
  /** 秒杀商品列表 */
  secKillProducts: ProductListItem[];
}

export interface ProductListResponse {
  statusCode: number;
  payload: ProductListPayload;
}

export interface GetProductListParams {
  clientTime: string; // 客户端本地时间，格式为 "2026-03-13T16:36:17+08:00"
}

// ========== 5.4 查询活动说明 ==========

export interface GetActivityDescParams {
  actId: string;
}

export interface ActivityDescPayload {
  introduction?: string;
  introductionTc?: string;
  introductionEn?: string;
  desc?: string;
  descTc?: string;
  descEn?: string;
  specialDesc?: string;
  specialDescTc?: string;
  specialDescEn?: string;
}

export interface ActivityDescResponse {
  statusCode: number;
  payload: ActivityDescPayload;
}

// ========== 5.5 查询秒杀商品详情 ==========

/** 商品状态：UPCOMING/REVEALING/ACTIVE/ENDED */
export type ProductDetailStatus = 'UPCOMING' | 'REVEALING' | 'ACTIVE' | 'ENDED';

export interface ProductDetailPayload {
  productId: string;
  productName: string;
  description: string;
  imageUrl: string;
  images: string[];
  originalPrice: number;
  seckillPrice: number;
  stock: number;
  totalStock: number;
  status: ProductDetailStatus;
  hasPreStart: boolean;
  startTime: number; // 毫秒
  endTime: number; // 毫秒
  countdownSeconds: number;
  displayStartTime: string;
  displayDate: string;
  secKillingConfig: SecKillingConfigProduct;
  coupon: CouponProduct & { description?: string };
  seckillInstruction: SeckillInstruction;
  purchaseLimit: number;
  hasPurchased: boolean;
}

export interface ProductDetailResponse {
  statusCode: number;
  payload: ProductDetailPayload;
}

export interface GetProductDetailParams {
  id: string | number;
  clientTime?: number;
}

// ========== 5.7 查询商品库存 ==========

export interface ProductStockPayload {
  productId: string;
  totalStock: number;
  remainStock: number;
  soldCount: number;
  progressPercent: number;
  /** 商品状态：UPCOMING/REVEALING/ACTIVE/ENDED */
  status: ProductDetailStatus | string;
}

export interface ProductStockResponse {
  statusCode: number;
  payload: ProductStockPayload;
}

// ========== 5.6 提交抢购请求 ==========

export interface SubmitOrderRequest {
  productId: string;
  actId: string;
  quantity: number;
  clientTime: string;
  timezone?: string;
}

export interface SubmitOrderPayload {
  memberId: string;
  productType: number;
  productId: string;
  quantity: number;
  queueStatus: 'PENDING' | 'PROCESSING';
  crmOrderId: number;
  hexOrderId?: string | null;
  queuePosition?: number;
  estimatedWaitSeconds?: number;
  message: string;
}

export interface SubmitOrderResponse {
  statusCode: number;
  payload: SubmitOrderPayload | null;
  code?: string; // PRODUCT_SOLD_OUT | RATE_LIMITED
  message?: string;
}

export interface SubmitOrderErrorPayload {
  retryAfter?: number;
}

// ========== 5.8 查询排队状态 ==========

export interface GetQueueStatusParams {
  requestId: string;
  actId: string;
  productId: string;
}

export enum QueueStatus {
  PENDING = 'PENDING', // 排队中
  PROCESSING = 'PROCESSING', // 处理中
  COMPLETED = 'COMPLETED', // 完成
  FAILED = 'FAILED', // 失败
}

export enum OrderStatus {
  UNPAID = 'UNPAID', // 未支付
  PAID = 'PAID', // 已支付
  CANCELLED = 'CANCELLED', // 已取消
}

export interface QueueStatusPayload {
  memberId: string;
  productType: number;
  productId: string;
  quantity: number;
  queueStatus: QueueStatus;
  /** 未建单时为 null，已建单时为 UNPAID/PAID/CANCELLED */
  orderStatus: OrderStatus | null;
  crmOrderId: number;
  hexOrderId: string | null;
  queuePosition?: number;
  estimatedWaitSeconds?: number;
  expireTime?: number;
  failReason?: string;
  message: string;
}

export interface QueueStatusResponse {
  statusCode: number;
  payload: QueueStatusPayload;
}

// ========== 5.9 查询秒杀结果 ==========

export interface SeckillResultPayload {
  hexOrderId: string;
  crmOrderId: number;
  memberId: string;
  productId: string;
  productName: string;
  quantity: number;
  seckillPrice?: number;
  originalPrice?: number;
  orderStatus: OrderStatus;
  expireTime?: number;
  remainSeconds?: number;
  payUrl?: string;
  payTime?: number;
  cancelReason?: string;
  message: string;
}

export interface SeckillResultResponse {
  statusCode: number;
  payload: SeckillResultPayload | null;
  code?: string; // NOT_FOUND
  message?: string;
}

// ========== 5.10 取消排队 ==========

export interface CancelQueueResponse {
  statusCode: number;
  message: string;
  payload: null;
  code?: string; // NOT_FOUND | INVALID_STATUS
}

// ========== 通用错误响应 ==========

export interface ApiErrorResponse {
  statusCode: number;
  code?: string;
  message: string;
  payload: unknown;
}
